'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import 'leaflet/dist/leaflet.css';
import { createClient } from '@/lib/supabase/client';

const NAVY = '#1C2E5E';
const CYAN = '#E89A3C';

type Haz = 'wind' | 'hail' | 'tornado' | 'all';
type Ev = { id: string; event_type: string; event_date: string; hail_size_in: number | null; wind_mph: number | null; city: string | null; county: string | null; state: string | null; lat: number; lon: number };
type Hit = Ev & { dist_mi: number };

const iso = (d: Date) => d.toISOString().slice(0, 10);

function evColor(ev: { event_type: string; wind_mph: number | null; hail_size_in: number | null }): string {
  if (ev.event_type === 'wind') {
    const w = ev.wind_mph;
    if (w == null) return '#64748b';   // UNK — reported wind DAMAGE (slate)
    if (w >= 90) return '#7c3aed';
    if (w >= 75) return '#dc2626';
    if (w >= 65) return '#f97316';
    return '#eab308';                  // 58–64 mph
  }
  if (ev.event_type === 'tornado') return '#0f172a';
  const s = ev.hail_size_in || 0;
  if (s >= 3) return '#7c3aed'; if (s >= 2) return '#dc2626'; if (s >= 1.5) return '#f97316'; if (s >= 1) return '#eab308'; return '#22c55e';
}
function evRadius(ev: { event_type: string; wind_mph: number | null; hail_size_in: number | null }): number {
  if (ev.event_type === 'wind') { const w = ev.wind_mph; return w == null ? 4 : Math.max(3, Math.min(13, (w - 45) / 5)); }
  if (ev.event_type === 'tornado') return 7;
  const s = ev.hail_size_in || 0; return Math.max(3, Math.min(13, 3 + s * 2.5));
}
function evMag(ev: { event_type: string; wind_mph: number | null; hail_size_in: number | null }): string {
  if (ev.event_type === 'wind') return ev.wind_mph ? `${ev.wind_mph} mph wind` : 'Wind damage';
  if (ev.event_type === 'tornado') return 'Tornado';
  return ev.hail_size_in ? `${ev.hail_size_in.toFixed(2)}" hail` : 'Hail';
}

const LEGENDS: Record<Haz, [string, string][]> = {
  wind: [['Damage reported (no gust)', '#64748b'], ['58–64 mph', '#eab308'], ['65–74 mph (roof damage)', '#f97316'], ['75–89 mph', '#dc2626'], ['90+ mph (extreme)', '#7c3aed']],
  hail: [['< 1"', '#22c55e'], ['1–1.5"', '#eab308'], ['1.5–2" (roof damage)', '#f97316'], ['2–3"', '#dc2626'], ['3"+', '#7c3aed']],
  tornado: [['Tornado report', '#0f172a']],
  all: [['Wind', '#f97316'], ['Hail', '#22c55e'], ['Tornado', '#0f172a']],
};

export default function StormMapInteractive() {
  const supabase = useMemo(() => createClient(), []);
  const mapEl = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<any>(null);
  const layerRef = useRef<any>(null);
  const pinRef = useRef<any>(null);

  const [hazard, setHazard] = useState<Haz>('wind');
  const [events, setEvents] = useState<Ev[]>([]);
  const [loading, setLoading] = useState(true);
  const [start, setStart] = useState(iso(new Date(Date.now() - 30 * 86400000)));
  const [end, setEnd] = useState(iso(new Date()));
  const [minMag, setMinMag] = useState(0);   // mph when wind, inches when hail
  const [q, setQ] = useState('');
  const [searching, setSearching] = useState(false);
  const [lookup, setLookup] = useState<{ label: string; hits: Hit[] } | null>(null);

  // Load storm events for the hazard + window.
  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true);
      let query = supabase.from('storm_events')
        .select('id,event_type,event_date,hail_size_in,wind_mph,city,county,state,lat,lon')
        .gte('event_date', start).lte('event_date', end)
        .order('event_date', { ascending: false }).limit(15000).abortSignal(ctrl.signal);
      if (hazard !== 'all') query = query.eq('event_type', hazard);
      const { data } = await query;
      setEvents((data as Ev[]) || []);
      setLoading(false);
    })();
    return () => ctrl.abort();
  }, [supabase, start, end, hazard]);

  // Init Leaflet (canvas renderer handles the 10k+ points smoothly).
  useEffect(() => {
    if (mapRef.current || !mapEl.current) return;
    const L = require('leaflet');
    const map = L.map(mapEl.current, { scrollWheelZoom: true, preferCanvas: true }).setView([39.5, -98.35], 4);
    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', { attribution: '&copy; OSM &copy; CARTO', maxZoom: 19 }).addTo(map);
    layerRef.current = L.layerGroup().addTo(map);
    map.on('click', (e: any) => runLookup(e.latlng.lat, e.latlng.lng, `Pin @ ${e.latlng.lat.toFixed(3)}, ${e.latlng.lng.toFixed(3)}`));
    mapRef.current = map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const passesMin = (e: Ev) => {
    if (hazard === 'wind' || (hazard === 'all' && e.event_type === 'wind')) return e.wind_mph == null || e.wind_mph >= minMag;
    if (hazard === 'hail' || (hazard === 'all' && e.event_type === 'hail')) return (e.hail_size_in || 0) >= minMag;
    return true;
  };

  // Redraw markers.
  useEffect(() => {
    const L = require('leaflet');
    const layer = layerRef.current; if (!layer) return;
    layer.clearLayers();
    events.filter(passesMin).forEach(e => {
      L.circleMarker([e.lat, e.lon], { radius: evRadius(e), color: evColor(e), fillColor: evColor(e), fillOpacity: 0.6, weight: 0.5 })
        .bindPopup(`<b>${evMag(e)}</b><br/>${e.city || ''}${e.county ? ', ' + e.county + ' Co' : ''} ${e.state || ''}<br/>${e.event_date}`)
        .addTo(layer);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events, minMag, hazard]);

  async function runLookup(lat: number, lon: number, label: string) {
    const L = require('leaflet');
    const map = mapRef.current;
    if (pinRef.current) map.removeLayer(pinRef.current);
    pinRef.current = L.marker([lat, lon]).addTo(map);
    const { data } = await supabase.rpc('storms_near_point', { p_lat: lat, p_lon: lon, p_radius_mi: 3, p_start: start, p_end: end, p_type: hazard === 'all' ? null : hazard });
    setLookup({ label, hits: (data as Hit[]) || [] });
  }

  async function search(e: React.FormEvent) {
    e.preventDefault(); if (!q.trim()) return;
    setSearching(true);
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/search?format=json&countrycodes=us&limit=1&q=${encodeURIComponent(q)}`);
      const j = await r.json();
      if (j?.[0]) { const lat = parseFloat(j[0].lat), lon = parseFloat(j[0].lon); mapRef.current?.setView([lat, lon], 11); runLookup(lat, lon, j[0].display_name); }
      else setLookup({ label: q, hits: [] });
    } catch { /* ignore */ } finally { setSearching(false); }
  }

  const shown = events.filter(passesMin);
  const maxWind = shown.reduce((m, e) => Math.max(m, e.wind_mph || 0), 0);
  const maxHail = shown.reduce((m, e) => Math.max(m, e.hail_size_in || 0), 0);
  const isWind = hazard === 'wind';
  const isHail = hazard === 'hail';

  const HAZ_TABS: [Haz, string][] = [['wind', 'Wind'], ['hail', 'Hail'], ['tornado', 'Tornado'], ['all', 'All']];

  return (
    <div className="p-4 md:p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: NAVY }}>Storm Map — Wind &amp; Hail Intelligence</h1>
          <p className="text-sm text-gray-500">Live NOAA/NWS storm reports. Click the map or search an address to check storm history.</p>
        </div>
        <div className="flex gap-4 text-center">
          <div><div className="text-xl font-bold" style={{ color: CYAN }}>{loading ? '…' : shown.length.toLocaleString()}</div><div className="text-xs text-gray-400">reports</div></div>
          <div><div className="text-xl font-bold" style={{ color: NAVY }}>{isHail ? (maxHail ? maxHail.toFixed(2) + '"' : '—') : (maxWind ? maxWind + ' mph' : '—')}</div><div className="text-xs text-gray-400">peak</div></div>
        </div>
      </div>

      {/* hazard tabs */}
      <div className="flex gap-1.5 mb-3">
        {HAZ_TABS.map(([h, label]) => (
          <button key={h} onClick={() => { setHazard(h); setMinMag(0); }}
            className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${hazard === h ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
            style={hazard === h ? { background: NAVY } : {}}>{label}</button>
        ))}
      </div>

      {/* filters */}
      <div className="flex flex-wrap items-end gap-3 mb-3 bg-gray-50 rounded-xl p-3">
        <label className="text-xs text-gray-600">From<input type="date" value={start} max={end} onChange={e => setStart(e.target.value)} className="block mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm" /></label>
        <label className="text-xs text-gray-600">To<input type="date" value={end} min={start} onChange={e => setEnd(e.target.value)} className="block mt-1 px-2 py-1.5 rounded-lg border border-gray-200 text-sm" /></label>
        {(isWind || isHail) && (
          <label className="text-xs text-gray-600">Min {isWind ? 'gust' : 'size'}: <b>{isWind ? `${minMag} mph` : `${minMag.toFixed(2)}"`}</b>
            <input type="range" min={0} max={isWind ? 100 : 3} step={isWind ? 5 : 0.25} value={minMag} onChange={e => setMinMag(parseFloat(e.target.value))} className="block mt-1 w-44" />
          </label>
        )}
        <form onSubmit={search} className="flex gap-2 ml-auto">
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search an address…" className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm w-56" />
          <button disabled={searching} className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ background: NAVY }}>{searching ? '…' : 'Check storms'}</button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        <div ref={mapEl} className="rounded-xl border border-gray-200 z-0" style={{ height: '620px' }} />

        <div className="space-y-3">
          <div className="bg-white rounded-xl border border-gray-100 p-3 text-xs">
            <div className="font-semibold text-gray-700 mb-2">{hazard === 'all' ? 'Hazard' : isWind ? 'Wind gust' : isHail ? 'Hail size' : 'Legend'}</div>
            {LEGENDS[hazard].map(([l, c]) => (
              <div key={l} className="flex items-center gap-2 mb-1"><span className="w-3 h-3 rounded-full inline-block" style={{ background: c }} />{l}</div>
            ))}
          </div>

          <div className="bg-white rounded-xl border border-gray-100 p-3">
            <div className="font-semibold text-gray-700 text-sm mb-1">Address storm check</div>
            {!lookup ? (
              <p className="text-xs text-gray-400">Search an address or click the map to see storm reports within 3 miles.</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2 line-clamp-2">{lookup.label}</p>
                {lookup.hits.length === 0 ? (
                  <p className="text-sm text-gray-500">No {hazard === 'all' ? 'storm' : hazard} reports within 3 mi in this window.</p>
                ) : (
                  <>
                    <p className="text-sm mb-2"><b>{lookup.hits.length}</b> report{lookup.hits.length > 1 ? 's' : ''} nearby.</p>
                    <div className="max-h-72 overflow-auto space-y-1.5">
                      {lookup.hits.map(h => (
                        <div key={h.id} className="text-xs flex items-center justify-between gap-2 border-b border-gray-50 pb-1">
                          <span><span className="font-semibold" style={{ color: evColor(h) }}>{evMag(h)}</span> · {h.event_date}</span>
                          <span className="text-gray-400">{h.dist_mi} mi</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
